import { useState } from "react";
import { Alert, Badge, Button, Checkbox, Code, CopyButton, Group, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import { useForm } from "@mantine/form";
import { toast } from "react-hot-toast";
import { useFetch } from "../../helpers/useFetch.js";
import { IconAlertCircle, IconCheck, IconCopy, IconKey, IconMail, IconUser } from "@tabler/icons-react";

function CopyRow({ icon: Icon, label, value }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Group gap="xs" wrap="nowrap">
        <Icon size={16} stroke={1.5} style={{ flexShrink: 0 }} />
        <Text fw={600} size="sm" style={{ flexShrink: 0 }}>
          {label}
        </Text>
        <Code>{value}</Code>
      </Group>
      <CopyButton value={value} timeout={2000}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Zkopírováno" : "Kopírovat"}>
            <Button variant="subtle" size="compact-xs" color={copied ? "teal" : "gray"} onClick={copy}>
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </Button>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

export default function GameAccountModal({ context, id, innerProps }) {
  const { account, typeName } = innerProps;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const form = useForm({
    initialValues: {
      is_not_functional: !account.is_functional,
      note: account.note ?? "",
    },
  });

  async function handleSubmit(values) {
    setLoading(true);
    const res = await useFetch(`/gamingaccounts/${account.id}`, "PUT", {
      is_functional: !values.is_not_functional,
      note: values.note || null,
    });
    setLoading(false);
    if (res?.error) {
      setError(res.error);
    } else {
      toast.success("Účet byl upraven.");
      context.closeModal(id);
    }
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            Typ účtu:
          </Text>
          <Badge variant="light">{typeName}</Badge>
          {!account.is_functional && (
            <Badge color="red" variant="light">
              Nefunkční
            </Badge>
          )}
        </Group>

        <Stack gap="xs">
          <CopyRow icon={IconUser} label="Nick" value={account.nick} />
          <CopyRow icon={IconKey} label="Heslo" value={account.password} />
          {account.email && <CopyRow icon={IconMail} label="Email" value={account.email} />}
        </Stack>

        <Checkbox
          label="Označit účet jako nefunkční"
          color="red"
          mt="xs"
          {...form.getInputProps("is_not_functional", { type: "checkbox" })}
        />

        <Textarea
          label="Poznámka"
          placeholder="Volitelná poznámka k účtu..."
          minRows={2}
          autosize
          {...form.getInputProps("note")}
        />

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} stroke={1.5} />}
            color="red"
            withCloseButton
            onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Button type="submit" loading={loading} fullWidth>
          Uložit změny
        </Button>
      </Stack>
    </form>
  );
}
